import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router';
import { confirmLeave } from './utils/leaveGuard';
import { Layout, Menu, Button, Avatar, Dropdown, Modal, Radio, Space, Card, Spin, Tooltip } from 'antd';
import {
  DatabaseOutlined,
  LogoutOutlined,
  UserOutlined,
  TeamOutlined,
  SettingOutlined,
  SafetyOutlined,
  AppstoreOutlined,
  BgColorsOutlined,
  BankOutlined,
  RightOutlined,
  LeftOutlined,
  DashboardOutlined,
  CompassOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import SessionExpiredModal from './components/SessionExpiredModal';
import NotificationBell from './components/NotificationBell';
import AccessGate from './components/AccessGate';
import { canAccess } from './components/permissions';
import { getRouterBasename } from './utils/tenantPath';
import './App.css';

// Import pages
import SqlWorkbench from './pages/SqlWorkbench';
import UserManagement from './pages/UserManagement';
import UserSettings from './pages/UserSettings';
import Unauthorized from './pages/Unauthorized';
import EmailVerification from './pages/EmailVerification';

// Pipeline-plane pages (data ingestion: connectors, syncs, vault).
import NewConnectionWizard from './pages/NewConnectionWizard';
import DataExplorerPage from './pages/DataExplorerPage';
import DataSourcesPage from './pages/DataSourcesPage';
import DataCenter from './pages/DataCenter';
import DagsterPage from './pages/DagsterPage';
import { getEdition, isPro } from './services/deploymentConfigService';
import { EditionComparisonModal } from './pages/discovery/proUpgrade';
import { listTiers as fetchOrgTiers } from './services/orgStructureService';

const DISCOVERY_ORG = '/discovery/org';
const DISCOVERY_TEAM = '/discovery/team';
const DISCOVERY_INDIVIDUAL = '/discovery/individual';
const MY_DASHBOARD = '/my-dashboard';
const DATA_CENTER = '/data/center';
const ADMIN_ORG_STRUCTURE = '/admin/org-structure';
const PIPELINE_SOURCES = '/pipeline/sources';
const ADMIN_USERS = '/admin/users';
const ADMIN_SETTINGS_USERS = '/admin/settings/users';
const ADMIN_SETTINGS_DASHBOARD_LAYOUTS = '/admin/settings/dashboard-layouts';
const TRANSPARENT = 'transparent';

const THEME_PRIMARY = 'var(--theme-primary)';
const BORDER_STYLE = '1px solid var(--theme-border)';
const THEME_TEXT_PRIMARY = 'var(--theme-textPrimary)';
const SWATCH_BORDER = '1px solid rgba(0,0,0,0.1)';

// DISCOVERY — Individual / Team / Organization (3-page layout).
// Lazy-loaded so the bundle is only fetched when a user first
// opens a Discovery route (keeps the initial app bundle lean).
const IndividualView = lazy(() => import('./pages/discovery/IndividualView'));
const TeamView = lazy(() => import('./pages/discovery/TeamView'));
const OrgView = lazy(() => import('./pages/discovery/OrgView'));
const OrgStructure = lazy(() => import('./pages/OrgStructure'));
const TierDashboard = lazy(() => import('./pages/discovery/TierDashboard'));
// MY DASHBOARD — personal landing page composed from shared Discovery sections.
const MyDashboard = lazy(() => import('./pages/discovery/MyDashboard'));
const DashboardLayouts = lazy(() => import('./pages/DashboardLayouts'));

const { Content, Sider } = Layout;

// DISCOVERY tree connector geometry — kept in sync with the matching CSS
// rule (.discovery-tree-li / .discovery-tree-branch in App.css). The
// connector is an absolutely-positioned box (not a flex sibling), so its
// position math only has to agree with these numbers, not with anything
// layout-computed.
const DISCOVERY_TREE_ROW_HEIGHT = 36; // .discovery-tree-li height
const DISCOVERY_TREE_LI_PADDING = 16; // .ant-menu-submenu-popup .ant-menu-item padding-left
const DISCOVERY_TREE_SLOT = 22;       // indent step per depth + connector width
// Measured live (getBoundingClientRect in a real browser, not guessed):
// the rendered icon glyph is 14px square, so half its width/height is 7px.
// A prior guess of 10 here overshot into the icon, and both the 2px and
// 5px end-gap attempts undershot it (< 7 never clears the glyph at all —
// it lands inside it either way), which is why the connector kept looking
// like it touched the icon regardless of which of those two it used.
const DISCOVERY_TREE_ICON_HALF = 7;
// Visible breathing room beyond the icon's edge, on top of clearing it.
const DISCOVERY_TREE_ICON_GAP = 3;
// Trimmed off each end of the connector (both the top, which would
// otherwise touch the icon above, and the right, which would otherwise
// touch the current icon): enough to clear the icon's own half-width/
// height plus the visible gap on top of that.
const DISCOVERY_TREE_END_GAP = DISCOVERY_TREE_ICON_HALF + DISCOVERY_TREE_ICON_GAP;

// ── Sidebar menu — pure helpers ─────────────────────────────────────────
// Module-level (explicit params, no closures over component state) so each
// stays independently readable/testable instead of piling onto AppContent's
// own branching.

/** One row of the DISCOVERY tree submenu, with its L-shaped connector to
 * the row above (see DISCOVERY_TREE_* geometry above). */
function buildDiscoveryTreeRow(item, idx) {
  return {
    key: item.key,
    path: item.path,
    text: item.text,
    // Every row (root included) gets the fixed height so the connector
    // maths below — which assumes rows are a contiguous, equal-height
    // stack with no gap between them — actually holds.
    className: 'discovery-tree-li',
    label: (
      <span className="discovery-tree-row" style={{ paddingLeft: idx * DISCOVERY_TREE_SLOT }}>
        {idx > 0 && (
          <span
            className="discovery-tree-branch"
            aria-hidden="true"
            style={{
              // Anchored to both icons' centers (left = the icon
              // above's center; left + width = the current icon's
              // center), then trimmed by END_GAP at the top and at
              // the right so it stops just short of each instead of
              // running into the glyph.
              left: DISCOVERY_TREE_LI_PADDING + (idx - 1) * DISCOVERY_TREE_SLOT + DISCOVERY_TREE_ICON_HALF,
              top: -(DISCOVERY_TREE_ROW_HEIGHT / 2) + DISCOVERY_TREE_END_GAP,
              height: DISCOVERY_TREE_ROW_HEIGHT - DISCOVERY_TREE_END_GAP,
              width: DISCOVERY_TREE_SLOT - DISCOVERY_TREE_END_GAP,
            }}
          />
        )}
        <span className="discovery-tree-icon">{item.iconEl}</span>
        <span className="discovery-tree-text">{item.text}</span>
      </span>
    ),
  };
}

/**
 * The DISCOVERY submenu is a straight chain following the org hierarchy —
 * Organization → each configured tier (top → leaf) → Individual — so it's
 * rendered as a tree (see buildDiscoveryTreeRow). ORGANIZATION (and the
 * legacy TEAM fallback) is admin-only (see canAccess), so a unit lead or
 * plain member never has it in their own list — filtering it out BEFORE
 * computing depth means whatever tier/INDIVIDUAL entry they actually land
 * on first renders as the root (no indent, no connector) instead of
 * looking like an orphaned child of a parent they can't see.
 */
function buildDiscoveryTreeItems(orgTiers, visibleTiers, userRole) {
  const rawChildren = [
    { key: 'discovery-org', iconEl: <BankOutlined />, text: 'ORGANIZATION', path: DISCOVERY_ORG },
    // Dynamic per-tier discoveries generated from the Org Structure tier
    // registry (top → leaf). Falls back to the static TEAM discovery when
    // no tiers are defined yet (un-configured tenant). Singular tierName
    // (e.g. "Team", "Project"), not the pluralLabel used elsewhere.
    ...(orgTiers.length > 0
      ? visibleTiers.map((t) => ({
          key: `discovery-tier-${t.tierLevel}`,
          iconEl: <TeamOutlined />,
          text: (t.tierName || t.pluralLabel || `TIER ${t.tierLevel}`).toUpperCase(),
          path: `/discovery/tier/${t.tierLevel}`,
        }))
      : [{ key: 'discovery-team', iconEl: <TeamOutlined />, text: 'TEAM', path: DISCOVERY_TEAM }]),
    { key: 'discovery-individual', iconEl: <UserOutlined />, text: 'INDIVIDUAL', path: DISCOVERY_INDIVIDUAL },
  ];
  return rawChildren
    .filter((item) => canAccess(userRole, item.path))
    .map((item, idx) => buildDiscoveryTreeRow(item, idx));
}

/** Build the sidebar menu (MY DASHBOARD / DISCOVERY / DATA / USER / SETTINGS)
 * before permission filtering — see filterMenuItemsByRole. */
function buildAllMenuItems({ orgTiers, visibleTiers, userRole, isAdmin, isOrgMember }) {
  return [
    // MY DASHBOARD — personal landing page (top of the menu) that reuses the
    // Discovery insight components. Open to every authenticated user.
    { key: 'my-dashboard', icon: <DashboardOutlined />, label: 'MY DASHBOARD', path: MY_DASHBOARD },
    {
      key: 'discovery',
      icon: <CompassOutlined />,
      label: 'DISCOVERY',
      children: buildDiscoveryTreeItems(orgTiers, visibleTiers, userRole),
    },
    {
      key: 'data',
      icon: <DatabaseOutlined />,
      label: 'DATA',
      children: [
        { key: 'data-center', label: 'DATA CENTER', path: DATA_CENTER },
        // Org Structure: shown to admins and any org member (canAccess allows
        // the route for all, so this membership check is what hides it from
        // users who have no org row).
        ...((isAdmin || isOrgMember)
          ? [{ key: 'data-org-structure', label: 'ORG STRUCTURE', path: ADMIN_ORG_STRUCTURE }]
          : []),
        { key: 'data-ingest', label: 'INGEST DATA', path: PIPELINE_SOURCES },
      ],
    },
    { key: 'users', icon: <TeamOutlined />, label: 'USER', path: ADMIN_USERS },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'SETTINGS',
      children: [
        { key: 'settings-users', icon: <SafetyOutlined />, label: 'USER SETTINGS', path: ADMIN_SETTINGS_USERS },
        // My Dashboard layouts per role — a Pro capability, hidden on Standard.
        ...(isPro()
          ? [{ key: 'settings-dashboard-layouts', icon: <AppstoreOutlined />, label: 'DASHBOARD LAYOUTS', path: ADMIN_SETTINGS_DASHBOARD_LAYOUTS }]
          : []),
      ],
    },
  ];
}

export function hasNestedChildren(item) {
  // Nested submenus have no path/onClick of their own — keep them so
  // non-admin roles still see the submenu, and let their leaves be
  // evaluated on their own merits.
  return Array.isArray(item.children) && item.children.length > 0;
}

export function isStructuralMenuChild(child) {
  return child.type === 'group' || child.type === 'divider' || !!child.onClick;
}

function filterMenuChildren(children, userRole) {
  const filtered = children.filter((child) =>
    isStructuralMenuChild(child) || hasNestedChildren(child) || canAccess(userRole, child.path)
  );
  // Keep only if there are real items (not just the group header)
  const hasRealItems = filtered.some((child) => child.type !== 'group' && child.type !== 'divider');
  return hasRealItems ? filtered : null;
}

/** True when a divider ended up orphaned (leading/trailing, or doubled up)
 * once permission filtering dropped the section(s) around it. */
export function isOrphanDivider(item, idx, arr) {
  if (item.type !== 'divider') {return false;}
  const prev = arr[idx - 1];
  if (!prev || prev.type === 'divider') {return true;}
  const hasNextReal = arr.slice(idx + 1).some((i) => i.type !== 'divider');
  return !hasNextReal;
}

/** Filter the sidebar menu down to what `userRole` can access. */
export function filterMenuItemsByRole(allMenuItems, userRole) {
  return allMenuItems
    .map((item) => {
      if (item.children) {
        const filteredChildren = filterMenuChildren(item.children, userRole);
        return filteredChildren ? { ...item, children: filteredChildren } : null;
      }
      if (item.type === 'divider') {return item;}
      return canAccess(userRole, item.path) ? item : null;
    })
    .filter(Boolean)
    // Drop orphan dividers left behind when a whole section was filtered
    // out by permissions: no leading/trailing dividers and no two in a row.
    .filter((item, idx, arr) => !isOrphanDivider(item, idx, arr));
}

/** Find a menu entry (with path or onClick) by key. */
export function findMenuEntry(items, key) {
  for (const item of items) {
    if (item.key === key && (item.path || item.onClick)) {return item;}
    if (item.children) {
      const child = findMenuEntry(item.children, key);
      if (child) {return child;}
    }
  }
  return null;
}

/** Selected sidebar key(s) for the current path. */
export function resolveSelectedMenuKeys(menuItems, pathname) {
  for (const item of menuItems) {
    if (item.path === pathname) {return [item.key];}
    if (item.children) {
      const child = item.children.find((c) => c.path === pathname);
      if (child) {return [child.key];}
    }
  }
  return [];
}

const stripQueryFromPath = (p) => (p || '').split('?')[0];

/** Menu label for the current path, walking the same structure used for the
 * sidebar so the browser tab title stays in sync with it. */
export function findMenuLabelForPath(items, pathname) {
  for (const item of items) {
    if (item.path && stripQueryFromPath(item.path) === pathname) {return item.text || item.label;}
    if (item.children) {
      const child = item.children.find((c) => c.path && stripQueryFromPath(c.path) === pathname);
      if (child) {return child.text || child.label;}
    }
  }
  return null;
}

// Routes that aren't direct menu items (sub-routes, wizards, redirects).
const FALLBACK_PAGE_TITLES = {
  '/pipeline/connections/new': 'NEW CONNECTION',
};

export function resolvePageTitle(allMenuItems, pathname) {
  const label = findMenuLabelForPath(allMenuItems, pathname) || FALLBACK_PAGE_TITLES[pathname] || null;
  return label ? `AI FinOps Master | ${label}` : 'AI FinOps Master';
}

/**
 * Route table for the authenticated app. Each entry is either a redirect
 * (`element`) or a page (`Component`, optionally gated by `permission` via
 * canAccess — omitting it means the route is always visible, e.g.
 * TierDashboard checks access internally). Kept as data so the render below
 * is one `.map()` over a flat list instead of dozens of hand-written,
 * near-identical <Route> elements — that repetition, not real decision
 * logic, was almost all of this file's route-definition complexity.
 */
const ROUTE_CONFIG = [
  { path: '/', element: <Navigate to="/my-dashboard" replace /> },

  { path: MY_DASHBOARD, Component: MyDashboard, permission: MY_DASHBOARD },

  { path: DATA_CENTER, Component: DataCenter, permission: DATA_CENTER },
  { path: '/data/sql', Component: SqlWorkbench, permission: '/data/sql' },

  // DISCOVERY — Individual / Team / Organization (3-page layout).
  { path: '/discovery', element: <Navigate to="/discovery/individual" replace /> },
  { path: DISCOVERY_INDIVIDUAL, Component: IndividualView, permission: DISCOVERY_INDIVIDUAL },
  { path: DISCOVERY_TEAM, Component: TeamView, permission: DISCOVERY_TEAM },
  { path: DISCOVERY_ORG, Component: OrgView, permission: DISCOVERY_ORG },
  { path: '/discovery/tier/:level', Component: TierDashboard },
  // Legacy AI Finance routes → Discovery pages (preserve specific targets).
  { path: '/ai-finance', element: <Navigate to="/discovery/individual" replace /> },
  { path: '/ai-finance/team', element: <Navigate to="/discovery/team" replace /> },
  { path: '/ai-finance/org', element: <Navigate to="/discovery/org" replace /> },
  { path: '/ai-finance/roi', element: <Navigate to="/discovery/org?tab=productivity" replace /> },
  { path: '/ai-finance/*', element: <Navigate to="/discovery/individual" replace /> },
  { path: '/ai-finance-v2/team', element: <Navigate to="/discovery/team" replace /> },
  { path: '/ai-finance-v2/org', element: <Navigate to="/discovery/org" replace /> },
  { path: '/ai-finance-v2/*', element: <Navigate to="/discovery/individual" replace /> },

  { path: ADMIN_ORG_STRUCTURE, Component: OrgStructure, permission: ADMIN_ORG_STRUCTURE },
  { path: ADMIN_USERS, Component: UserManagement, permission: ADMIN_USERS },
  { path: ADMIN_SETTINGS_USERS, Component: UserSettings, permission: ADMIN_SETTINGS_USERS },
  { path: ADMIN_SETTINGS_DASHBOARD_LAYOUTS, Component: DashboardLayouts, permission: ADMIN_SETTINGS_DASHBOARD_LAYOUTS },

  // Pipeline plane (data ingestion)
  { path: '/pipeline', element: <Navigate to="/pipeline/sources" replace /> },
  { path: PIPELINE_SOURCES, Component: DataSourcesPage, permission: PIPELINE_SOURCES },
  // Legacy routes — keep working but funnel into the unified page.
  { path: '/pipeline/connections', element: <Navigate to="/pipeline/sources" replace /> },
  // Not on the roadmap and not linked from anywhere: adding a source goes
  // through DataSourcesPage's own inline form, so this guided wizard is a
  // second implementation of the same feature that no menu reaches. Kept
  // rather than deleted, but gated behind the page it belongs to — without a
  // `permission` it was the one /pipeline/* route a plain member could open
  // by typing the URL, and it creates a connection at step 1.
  {
    path: '/pipeline/connections/new',
    Component: NewConnectionWizard,
    permission: PIPELINE_SOURCES,
  },
  { path: '/pipeline/catalog', element: <Navigate to="/pipeline/sources?add=1" replace /> },
  { path: '/pipeline/syncs', element: <Navigate to="/pipeline/sources" replace /> },
  { path: '/pipeline/syncs-dagit', element: <Navigate to="/pipeline/sources" replace /> },
  { path: '/pipeline/syncs-legacy', element: <Navigate to="/pipeline/sources" replace /> },
  { path: '/pipeline/data-explorer', Component: DataExplorerPage },

  // Dagster (Dagit UI) — iframe embedded inside the tenant console.
  //
  // `permission` was missing until 2026-09-22, which made this the one
  // /pipeline/* route any signed-in member could open by typing the URL. The
  // backend 403s a non-admin so nothing leaked, but the page belongs to
  // Pipeline Sources and is now gated with it. DagsterPage additionally
  // renders nothing useful when the Dagit proxy is switched off — it reads
  // isDagitEnabled() itself, since ROUTE_CONFIG is evaluated at module load,
  // before the deployment config has been fetched.
  {
    path: '/pipeline/dagster',
    Component: DagsterPage,
    permission: PIPELINE_SOURCES,
  },

  { path: '*', element: <Navigate to="/my-dashboard" replace /> },
];

/** Resolve one ROUTE_CONFIG entry to its element: a fixed redirect, an
 * always-visible page, or a permission-gated page (Unauthorized otherwise). */
export function resolveRouteElement(route, userRole) {
  if (route.element) {return route.element;}
  const { Component, permission } = route;
  if (!permission) {return <Component />;}
  return canAccess(userRole, permission) ? <Component /> : <Unauthorized />;
}

// Org-position scope filters the per-tier Discovery menu: a non-admin only
// sees tiers they have a visible unit at/under. Admins (and an un-enforced /
// not-yet-loaded scope) see every tier.
function visibleOrgTiers(orgTiers, orgScope) {
  const orgEnforced = !!(orgScope && orgScope.enforced && !orgScope.isAdmin);
  return orgEnforced
    ? orgTiers.filter((t) => (orgScope.visibleTierLevels || []).includes(t.tierLevel))
    : orgTiers;
}

// The profile dropdown: who is signed in, the edition, theme and logout.
function buildUserMenuItems({ userLabel, userRole, onEdition, onTheme, logout }) {
  const infoItemStyle = {
    color: 'var(--theme-text)',
    cursor: 'default',
    opacity: 1,
  };
  return [
    {
      key: 'user',
      icon: <UserOutlined style={{ color: THEME_PRIMARY }} />,
      label: (
        <span>
          {userLabel}
          <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 11, textTransform: 'capitalize' }}>
            · {userRole || 'user'}
          </span>
        </span>
      ),
      style: infoItemStyle,
    },
    {
      key: 'edition',
      icon: <CrownOutlined style={{ color: isPro() ? THEME_PRIMARY : '#94a3b8', marginInlineEnd: 0 }} />,
      label: (
        <span>
          Edition
          <span style={{ marginLeft: 6, opacity: 0.7, fontSize: 11, textTransform: 'capitalize' }}>
            · {getEdition()}
          </span>
        </span>
      ),
      // Clickable — opens the Standard-vs-Pro comparison dialog.
      onClick: onEdition,
    },
    { type: 'divider' },
    {
      key: 'theme',
      icon: <BgColorsOutlined />,
      label: 'Theme',
      onClick: onTheme,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
    },
  ];
}

// The logo block at the top of the sider: the mark alone when collapsed.
function SiderBrand({ navCollapsed }) {
  return (
    <div style={{
      height: 60,
      borderBottom: BORDER_STYLE,
      background: THEME_PRIMARY,
      display: 'flex',
      alignItems: 'center',
      justifyContent: navCollapsed ? 'center' : 'space-between',
      padding: navCollapsed ? 0 : '0 12px 0 14px',
      gap: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
        <img
          src={`${process.env.PUBLIC_URL}/logo-white.svg`}
          alt="AI FinOps Master logo"
          width={navCollapsed ? 36 : 40}
          height={navCollapsed ? 36 : 40}
          style={{ flexShrink: 0 }}
        />
        {!navCollapsed && (
          <span style={{ color: '#ffffff', fontWeight: 700, fontSize: 17, lineHeight: 1.15, whiteSpace: 'nowrap' }}>
            AI FinOps<br />Master
          </span>
        )}
      </div>
    </div>
  );
}

// The collapse switcher and the profile row at the bottom of the sider.
function SiderFooter({ navCollapsed, toggleNav, userMenuItems, userLabel }) {
  return (
    <div style={{
      position: 'absolute',
      bottom: 16,
      left: 0,
      right: 0,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: navCollapsed ? 'center' : 'flex-end',
        padding: navCollapsed ? '0 0 8px' : '0 12px 8px',
      }}>
        <Tooltip title={navCollapsed ? 'Expand menu' : 'Collapse menu'} placement="right">
          <Button
            type="text"
            size="small"
            aria-label={navCollapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
            icon={navCollapsed
              ? <RightOutlined style={{ color: THEME_TEXT_PRIMARY }} />
              : <LeftOutlined style={{ color: THEME_TEXT_PRIMARY }} />}
            onClick={toggleNav}
          />
        </Tooltip>
      </div>
      {/* Profile section — avatar plus the current user's email
          (email is only shown in the expanded sidebar). */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: navCollapsed ? 'center' : 'flex-start',
        alignItems: 'center',
        gap: 10,
        padding: navCollapsed ? '12px 0 0' : '12px 16px 0',
        borderTop: BORDER_STYLE,
      }}>
        <Dropdown menu={{ items: userMenuItems }} placement="topLeft" trigger={['click']}>
          <Avatar
            style={{ background: 'var(--theme-logoGradient)', cursor: 'pointer', flexShrink: 0 }}
            icon={<UserOutlined />}
          />
        </Dropdown>
        <NotificationBell />
        {!navCollapsed && (
          <span
            title={userLabel}
            style={{
              color: THEME_TEXT_PRIMARY,
              fontSize: 14,
              fontWeight: 500,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            {String(userLabel).split('@')[0]}
          </span>
        )}
      </div>
    </div>
  );
}

// Theme picker: light / dark mode, then a colour within it.
function ThemeModal({ open, onClose, themes, currentTheme, changeTheme }) {
  const lightThemes = Object.entries(themes).filter(([, t]) => t.mode === 'light');
  const darkThemes = Object.entries(themes).filter(([, t]) => t.mode === 'dark');
  const currentMode = themes[currentTheme].mode;
  return (
    <Modal
      title={<span><BgColorsOutlined style={{ marginRight: 8 }} />Select Theme</span>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={500}
    >
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <h4 style={{ marginBottom: 12 }}>Mode</h4>
          <Radio.Group
            value={currentMode}
            buttonStyle="solid"
            size="large"
            onChange={(e) => {
              const newMode = e.target.value;
              const firstThemeOfMode = newMode === 'light' ? lightThemes[0][0] : darkThemes[0][0];
              changeTheme(firstThemeOfMode);
            }}
          >
            <Radio.Button value="light">Light</Radio.Button>
            <Radio.Button value="dark">Dark</Radio.Button>
          </Radio.Group>
        </div>
        <div>
          <h4 style={{ marginBottom: 12 }}>Color</h4>
          <Radio.Group
            value={currentTheme}
            onChange={(e) => changeTheme(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
              {(currentMode === 'light' ? lightThemes : darkThemes).map(([key, theme]) => (
                <Card
                  key={key}
                  size="small"
                  hoverable
                  onClick={() => changeTheme(key)}
                  style={{
                    cursor: 'pointer',
                    border: currentTheme === key ? `2px solid ${theme.primary}` : '1px solid #d9d9d9',
                    background: currentTheme === key ? theme.background : TRANSPARENT,
                  }}
                >
                  <Radio value={key} style={{ width: '100%' }}>
                    <Space>
                      <span style={{ fontWeight: currentTheme === key ? 600 : 400 }}>{theme.name}</span>
                      <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                        <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.primary, border: SWATCH_BORDER }} />
                        <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.secondary, border: SWATCH_BORDER }} />
                        <div style={{ width: 24, height: 24, borderRadius: 4, background: theme.logoGradient, border: SWATCH_BORDER }} />
                      </div>
                    </Space>
                  </Radio>
                </Card>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </Space>
    </Modal>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, logout, userEmail, userProfile, userRole, orgScope, isOrgMember } = useAuth();
  const { themes, currentTheme, changeTheme } = useTheme();
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showEditionModal, setShowEditionModal] = useState(false);
  // Sidebar navigation collapse state. Two modes: expanded (labels +
  // icons, 220px) and collapsed (icon-only rail, 64px). EXPANDED by
  // default — only a previously-stored explicit 'true' collapses it, so a
  // fresh session (or any non-'true'/missing value) always starts expanded.
  const [navCollapsed, setNavCollapsed] = useState(() => {
    try {
      return localStorage.getItem('navCollapsed') === 'true';
    } catch {
      return false;
    }
  });
  const toggleNav = useCallback(() => {
    setNavCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem('navCollapsed', String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // A phone-width window starts on the icon rail, so the page keeps its width;
  // a wider one goes back to the stored choice. Neither is stored.
  const onNavBreakpoint = useCallback((narrow) => {
    let stored = false;
    try { stored = localStorage.getItem('navCollapsed') === 'true'; } catch { /* ignore */ }
    setNavCollapsed(narrow || stored);
  }, []);

  // Org-structure tiers drive the dynamic DISCOVERY menu (one discovery per
  // tier). Best-effort: when none are defined yet the menu falls back to the
  // static Individual / Team / Organization layout.
  const [orgTiers, setOrgTiers] = useState([]);
  useEffect(() => {
    let on = true;
    fetchOrgTiers()
      .then((r) => { if (on) {setOrgTiers((r.tiers || []).filter((t) => t.generatesDashboard));} })
      .catch(() => { /* keep static fallback */ });
    return () => { on = false; };
  }, []);

  // Org-position scope filters the per-tier Discovery menu: a non-admin only
  // sees tiers they have a visible unit at/under. Admins (and an un-enforced /
  // not-yet-loaded scope) see every tier.
  const visibleTiers = visibleOrgTiers(orgTiers, orgScope);

  // ── Sidebar menu ────────────────────────────────────────────────
  //   MY DASHBOARD
  //   DISCOVERY     (org / per-tier / individual)
  //   DATA          (data center / org structure / ingest)
  //   USER
  //   SETTINGS      (user settings)
  //
  // Built by the module-level helpers above (buildAllMenuItems /
  // filterMenuItemsByRole) — explicit params, no hidden closures.
  const allMenuItems = buildAllMenuItems({ orgTiers, visibleTiers, userRole, isAdmin, isOrgMember });
  const menuItems = filterMenuItemsByRole(allMenuItems, userRole);

  // Handle menu click with Ctrl+Click for new tab (path-based items only)
  const handleMenuClick = ({ key, domEvent }) => {
    const entry = findMenuEntry(menuItems, key);
    if (!entry) {return;}
    if (entry.path) {
      if (domEvent.ctrlKey || domEvent.metaKey || domEvent.button === 1) {
        window.open(entry.path, '_blank');
        domEvent.preventDefault();
        domEvent.stopPropagation();
      } else {
        confirmLeave(() => navigate(entry.path));
      }
    }
  };

  // Update browser tab title to reflect the current page (e.g. "AI FinOps Master | DATA CENTER").
  useEffect(() => {
    document.title = resolvePageTitle(allMenuItems, location.pathname);
    // orgTiers is in the deps because the dynamic per-tier dashboard labels
    // (/discovery/tier/:level) only appear in the menu once the async tier
    // fetch resolves — without it the tab title stays "AI FinOps Master" on those pages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, orgTiers]);

  // User dropdown menu items
  const userLabel = userEmail || userProfile?.display_name || 'User';
  const userMenuItems = buildUserMenuItems({
    userLabel, userRole, logout: () => confirmLeave(logout),
    onEdition: () => setShowEditionModal(true),
    onTheme: () => setShowThemeModal(true),
  });

  return (
    <Layout style={{ height: '100vh', overflow: 'hidden' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={navCollapsed}
        breakpoint="md"
        onBreakpoint={onNavBreakpoint}
        width={220}
        collapsedWidth={64}
        style={{
          position: 'relative',
          height: '100vh',
          overflow: 'visible',
          background: 'var(--theme-sidebarGradient)',
          borderRight: BORDER_STYLE,
        }}
      >
        <SiderBrand navCollapsed={navCollapsed} />
        <Menu
          // Collapsed: icon rail (inline + inlineCollapsed) with flyout
          // popups. Expanded: vertical mode so submenus ALSO open as
          // floating popups instead of expanding inline — this restores
          // the old popup behaviour and avoids the inline-accordion
          // making the menu tall enough to grow a second scrollbar.
          // antd only reads inlineCollapsed in inline mode and warns when it
          // is passed alongside mode="vertical", so pass it only when collapsed.
          mode={navCollapsed ? 'inline' : 'vertical'}
          {...(navCollapsed ? { inlineCollapsed: true } : {})}
          selectedKeys={resolveSelectedMenuKeys(menuItems, location.pathname)}
          style={{
            height: 'calc(100vh - 120px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            border: 'none',
            background: TRANSPARENT,
            width: navCollapsed ? 64 : 220,
            paddingTop: 12,
          }}
          onClick={handleMenuClick}
          items={menuItems}
        />

        {/* Bottom area — collapse/expand switcher sits directly above the
            profile row. */}
        <SiderFooter navCollapsed={navCollapsed} toggleNav={toggleNav} userMenuItems={userMenuItems} userLabel={userLabel} />
      </Sider>
      {/* Standard-vs-Pro comparison (opened from the profile-menu edition row) */}
      <EditionComparisonModal
        open={showEditionModal}
        onClose={() => setShowEditionModal(false)}
      />
      {/* Theme Selection Modal */}
      <ThemeModal
        open={showThemeModal} onClose={() => setShowThemeModal(false)}
        themes={themes} currentTheme={currentTheme} changeTheme={changeTheme}
      />
      <Layout className="site-layout" style={{ height: '100vh' }}>
        <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', position: 'relative' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
            <Content style={{ overflow: 'auto', height: '100vh', flex: 1, background: 'var(--theme-contentBg)' }}>
          <Suspense fallback={(
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
              <Spin size="large" />
            </div>
          )}>
          <Routes>
            {ROUTE_CONFIG.map((route) => (
              <Route key={route.path} path={route.path} element={resolveRouteElement(route, userRole)} />
            ))}
          </Routes>
          </Suspense>
            </Content>
          </div>
        </div>
      </Layout>
    </Layout>
  );
}

function AuthenticatedApp() {
  const { isAuthenticated, isInitializing } = useAuth();

  // Only the initial bootstrap token-validation shows the full-screen spinner.
  // An interactive login must NOT unmount AccessGate (isLoading toggles during
  // login) — otherwise the gate's local state, including the "Email Not
  // Verified" modal opened right after login() resolves, is destroyed.
  if (isInitializing) {
    return (
      <div style={{
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: TRANSPARENT,
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Router basename={getRouterBasename()}>
        <Routes>
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="*" element={<AccessGate />} />
        </Routes>
      </Router>
    );
  }
  return (
    <Router basename={getRouterBasename()}>
      <AppContent />
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AuthenticatedApp />
        <SessionExpiredModal />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
